import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { GraduationCap, PlayCircle, ChevronDown, ChevronUp } from "lucide-react";
import api, { errMsg, fmtDate, fileUrl } from "../../lib/api";
import { PageHeader, LoadingRows, EmptyState } from "../../components/Shared";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";

const ytEmbed = (u) => {
  const m = (u || "").match(/(?:v=|youtu\.be\/|shorts\/)([\w-]{6,})/);
  return m ? `https://www.youtube.com/embed/${m[1]}` : null;
};

function Player({ url }) {
  const yt = ytEmbed(url);
  if (yt) return <iframe src={yt} title="lesson" className="w-full aspect-video rounded-xl" allowFullScreen />;
  if (/^https?:\/\//.test(url || "")) return <video controls src={url} className="w-full aspect-video rounded-xl bg-black" />;
  return null;
}

export default function MyCourses() {
  const [courses, setCourses] = useState(null);
  const [open, setOpen] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/mkt/my-courses").then((r) => setCourses(r.data.courses)).catch((e) => { toast.error(errMsg(e)); setCourses([]); });
  }, []);

  return (
    <div className="space-y-2" data-testid="my-courses-page">
      <PageHeader title="My Courses" subtitle="Courses you have purchased on the marketplace" testid="my-courses-title" />
      {!courses ? <LoadingRows rows={3} /> : courses.length === 0 ? (
        <EmptyState title="No courses yet" body="Learn business, marketing, design and more from MILTROO experts." icon={GraduationCap}>
          <Button data-testid="my-courses-browse" onClick={() => navigate("/marketplace/search?tab=courses")} className="rounded-full font-bold gradient-bg text-white border-0">Browse Courses</Button>
        </EmptyState>
      ) : (
        <div className="space-y-4 max-w-4xl">
          {courses.map((c) => {
            const lessons = (c.modules || []).reduce((n, m) => n + (m.lessons?.length || 0), 0);
            const isOpen = open === c.id;
            return (
              <div key={c.id} data-testid={`my-course-${c.id}`} className="bg-card border border-border rounded-xl overflow-hidden">
                <button onClick={() => setOpen(isOpen ? "" : c.id)} className="w-full flex items-center gap-4 p-4 text-left hover:bg-muted/40 transition-colors">
                  {c.thumbnail
                    ? <img src={fileUrl(c.thumbnail)} alt="" className="h-16 w-24 object-cover rounded-lg shrink-0 hidden sm:block" />
                    : <div className="h-16 w-24 rounded-lg shrink-0 hidden sm:flex items-center justify-center" style={{ background: "linear-gradient(135deg,#007A4D,#2563EB)" }}><GraduationCap className="h-6 w-6 text-white" /></div>}
                  <div className="flex-1 min-w-0">
                    <p className="font-display font-bold text-sm truncate">{c.title}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">by {c.freelancer_name} · {lessons} lessons · {c.level} · Purchased {fmtDate(c.purchased_at)}</p>
                  </div>
                  {isOpen ? <ChevronUp className="h-4 w-4 shrink-0" /> : <ChevronDown className="h-4 w-4 shrink-0" />}
                </button>
                {isOpen && (
                  <div className="border-t border-border p-4 sm:p-6 space-y-6">
                    {(c.modules || []).map((m, mi) => (
                      <div key={mi}>
                        <p className="font-bold text-sm mb-3">Module {mi + 1}: {m.title}</p>
                        <div className="space-y-4">
                          {(m.lessons || []).map((l, li) => (
                            <div key={li} data-testid={`lesson-${c.id}-${mi}-${li}`} className="rounded-xl border border-border p-4 space-y-3">
                              <p className="font-semibold text-sm flex items-center gap-2"><PlayCircle className="h-4 w-4 text-primary" />{l.title}</p>
                              {l.video && <Player url={l.video} />}
                              {l.content && <p className="text-xs text-muted-foreground whitespace-pre-wrap leading-relaxed">{l.content}</p>}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
